import { useCallback, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import type { PageState, LocationLink, ServerMessage, SerializedToken } from '@roll1/shared';
import {
  SHEET_RP_MSG,
  type SheetRpField,
  type SheetRpListChangedEvent,
  type HudBoardConfig,
} from '@roll1/shared';
import type { Token, Stroke, FogReveal } from '../types';
import { useSessionContext } from '../contexts/SessionContext';
import { useCanvasContext } from '../contexts/CanvasContext';
import { useTokenContext } from '../contexts/TokenContext';
import { useChatContext } from '../contexts/ChatContext';
import { useInitiativeContext } from '../contexts/InitiativeContext';
import { useSheetContext } from '../contexts/SheetContext';
import { useI18n } from '../i18n';
import type { TranslationKeys } from '../i18n/types';
import { useAlertContext } from '../contexts/AlertContext';
import { useJournalContext } from '../contexts/JournalContext';
import { useUndoContext } from '../contexts/UndoContext';
import { serializeToken } from '../lib/token-utils';

interface SyncOptions {
  onJournalRevealed?: () => void;
  onInitiativePanelToggle?: (visible: boolean) => void;
  onPing?: (x: number, y: number, by: string, color?: string) => void;
}

export function useMultiplayerSync({ onJournalRevealed, onInitiativePanelToggle, onPing }: SyncOptions) {
  const { mp, pages, isGM, outgoingPageId, setSessionSettings, gameSystem } = useSessionContext();
  const { engine, drawing, fog, dynamicFog, dynamicFogTokensRef, globalIlluminationRef, wallsHook, locationLinks, mapObjects } = useCanvasContext();
  const { tokens, notes: notesHook, pins: pinsHook } = useTokenContext();
  const { chat, rolls, sounds } = useChatContext();
  const { initiative } = useInitiativeContext();
  const { sheets: charSheets, templates: tmpl } = useSheetContext();
  const { t } = useI18n();
  const alertCtx = useAlertContext();
  const journal = useJournalContext();
  const undoStack = useUndoContext();

  const fogSentEnabled = useRef(false);
  const lastCommandErrorRef = useRef<{ key: string; ts: number } | null>(null);
  const aiTypingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /** Save current hook state back into the page data store */
  const saveCurrentPageToStore = useCallback(() => {
    const pid = pages.viewingPageId;
    if (!pid) return;
    const existing = pages.getPageData(pid);
    if (!existing) return;
    const saved: any = {
      ...existing,
      tokens: tokens.tokens.map((t) => {
        const { imageEl, ...rest } = t;
        return rest;
      }),
      drawings: drawing.strokes.current,
      fog: { enabled: fog.enabled.current, mode: fog.fogModeRef.current, revealed: fog.revealed.current, closings: fog.closings.current, explored: dynamicFog.exploredRef.current },
      notes: notesHook.notes,
      pins: pinsHook.pins,
      walls: wallsHook.walls.current,
    };
    // Preserve bgColor from engine ref (may have been set via color picker)
    if (engine.bgColorRef.current) saved.bgColor = engine.bgColorRef.current;
    pages.setPageData(pid, saved);
  }, [pages, tokens.tokens, drawing, fog, notesHook.notes, pinsHook.pins, wallsHook.walls]);

  /** Load page data from store into hooks */
  const loadPageToHooks = useCallback(
    (page: PageState) => {
      tokens.setTokens(page.tokens.map((t) => ({ ...t, imageEl: null })));
      drawing.strokes.current = [...page.drawings];
      fog.enabled.current = page.fog.enabled;
      fog.revealed.current = [...page.fog.revealed];
      fog.closings.current = [...(page.fog.closings ?? [])];
      fog.fogModeRef.current = page.fog.mode ?? 'manual';
      fogSentEnabled.current = page.fog.enabled;
      notesHook.setNotes(page.notes ?? []);
      pinsHook.setPins(page.pins ?? []);
      wallsHook.walls.current = [...(page.walls ?? [])];
      locationLinks.setLinks(page.locationLinks ?? []);
      mapObjects.setObjects(page.mapObjects ?? []);
      engine.bgColorRef.current = (page as any).bgColor || null;
      // Dynamic fog: sync explored and invalidate caches
      dynamicFog.setExplored([...(page.fog.explored ?? [])]);
      dynamicFog.invalidateAll();
      if (page.mapUrl) {
        engine.loadMap(page.mapUrl);
      } else {
        engine.loadMap('');
      }
      fog.invalidateCache();
      drawing.invalidateCache();
      engine.markAllDirty();
    },
    [tokens, drawing, fog, dynamicFog, engine, notesHook, pinsHook, locationLinks, mapObjects],
  );

  // Track previous viewingPageId for page-switch save/load
  const prevViewingPageRef = useRef('');
  useEffect(() => {
    const prevId = prevViewingPageRef.current;
    const newId = pages.viewingPageId;
    if (!newId || prevId === newId) {
      prevViewingPageRef.current = newId;
      return;
    }
    if (prevId && pages.getPageData(prevId)) {
      const existing = pages.getPageData(prevId)!;
      pages.setPageData(prevId, {
        ...existing,
        tokens: tokens.tokens.map((t) => {
          const { imageEl, ...rest } = t;
          return rest;
        }),
        drawings: [...drawing.strokes.current],
        fog: { enabled: fog.enabled.current, mode: fog.fogModeRef.current, revealed: [...fog.revealed.current], closings: [...fog.closings.current], explored: [...dynamicFog.exploredRef.current] },
        walls: [...wallsHook.walls.current],
      });
    }
    const newPage = pages.getPageData(newId);
    if (newPage) loadPageToHooks(newPage);
    prevViewingPageRef.current = newId;
  }, [pages.viewingPageId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Apply server state when joining a session (once only)
  const initializedRef = useRef(false);
  useEffect(() => {
    if (!mp.serverState || initializedRef.current) return;
    initializedRef.current = true;
    const s = mp.serverState;
    const activePage = pages.initFromState(s);
    if (activePage) loadPageToHooks(activePage);
    if (s.initiative) {
      initiative.setEntries(s.initiative.entries || []);
      initiative.setCurrentIndex(s.initiative.currentIndex ?? -1);
      initiative.setRound(s.initiative.round || 0);
      // Restore the GM-opened initiative panel from the state snapshot —
      // the live initiative:panel-updated broadcast misses late joiners and
      // reloads, so an active combat's panel silently vanished for players.
      if (s.initiative.panelVisible && (s.initiative.entries?.length ?? 0) > 0) {
        onInitiativePanelToggle?.(true);
      }
    }
    if (s.characterSheets) {
      charSheets.setSheets(s.characterSheets);
    }
    if (s.settings) {
      setSessionSettings(s.settings);
    }
    if (s.alerts) {
      alertCtx.setAlerts(s.alerts);
    }
    if (s.chat?.length) {
      chat.initMessages(s.chat);
    }
    // Load journal entries from initial join data
    if (mp.initialJournal) {
      journal.setEntries(mp.initialJournal.entries);
    }
  }, [mp.serverState]); // eslint-disable-line react-hooks/exhaustive-deps

  // WS disconnect toast
  const prevStatusRef = useRef(mp.status);
  useEffect(() => {
    const prev = prevStatusRef.current;
    prevStatusRef.current = mp.status;
    if (mp.session && prev === 'connected' && mp.status === 'disconnected') {
      toast.error(t('session.connectionLost'));
      initializedRef.current = false;
    }
    if (mp.session && prev === 'disconnected' && mp.status === 'connected') {
      toast.success(t('session.connectionRestored'));
      // Reload current page to ensure canvas state is fresh after reconnect
      const pid = pages.viewingPageId;
      if (pid) {
        const page = pages.getPageData(pid);
        if (page) loadPageToHooks(page);
      }
    }
  }, [mp.status, mp.session]); // eslint-disable-line react-hooks/exhaustive-deps

  // Welcome messages
  const initialized = useRef(false);
  useEffect(() => {
    if (!initialized.current) {
      initialized.current = true;
      chat.addSystemMessage(t('chat.welcome'));
    }
  }, [chat]);

  // Listen for multiplayer messages to sync state
  useEffect(() => {
    return mp.onMessage((msg: ServerMessage) => {
      // ── Granular Roleplay broadcasts (Feature B-01) ─────────────
      // These message types are defined in shared/contracts/sheet.ts but
      // are not part of the legacy ServerMessage discriminated union, so
      // we narrow on the string `type` and short-circuit to the HUD store
      // before the main switch. Server-side filterSheetForRole already
      // suppresses delivery for viewers with viewOtherSheets==='basic'.
      const rpType = (msg as { type: string }).type;
      // rp echoes mirror into the per-sheet charSheets store: the adapter-mode
      // HUD derives its rp view from sheet.values.roleplay_json, so this is the
      // only route that updates a live HUD on every client that receives the
      // echo (GM + owner). An earlier mirroring attempt hit a render cascade
      // (React #185) through the overlay's pendingPatch reconcile; that effect
      // is reference-stabilised now (pruneConfirmed + Object.is bail-out in
      // CharacterHudOverlay), and applyRpMutation preserves sheet identity on
      // no-op echoes, so a re-delivered echo cannot loop.
      if (rpType === SHEET_RP_MSG.fieldUpdated) {
        const m = msg as unknown as { sheetId: string; field: SheetRpField; value: string };
        charSheets.handleRpFieldUpdated(m.sheetId, m.field, m.value);
        return;
      }
      if (rpType === SHEET_RP_MSG.listChanged) {
        const m = msg as unknown as SheetRpListChangedEvent;
        if (m.op === 'add') {
          charSheets.handleRpListChanged(m.sheetId, m.listKey, 'add', { item: m.item as Record<string, unknown> });
        } else if (m.op === 'update') {
          charSheets.handleRpListChanged(m.sheetId, m.listKey, 'update', { itemId: m.itemId, changes: m.changes as Record<string, unknown> });
        } else {
          charSheets.handleRpListChanged(m.sheetId, m.listKey, 'remove', { itemId: m.itemId });
        }
        return;
      }
      if (rpType === SHEET_RP_MSG.boardConfigUpdated) {
        const m = msg as unknown as { sheetId: string; config: HudBoardConfig };
        charSheets.handleRpBoardConfigUpdated(m.sheetId, m.config as unknown as Record<string, unknown>);
        return;
      }

      switch (msg.type) {
        case 'token:moved':
          if (msg.pageId === pages.viewingPageId) {
            tokens.updateToken(msg.tokenId, { x: msg.x, y: msg.y });
            // Sync dynamic fog tokens ref
            const movedFogToken = dynamicFogTokensRef.current.find(t => t.id === msg.tokenId);
            if (movedFogToken) { movedFogToken.x = msg.x; movedFogToken.y = msg.y; }
            dynamicFog.invalidateVisibility();
          } else {
            const pd = pages.getPageData(msg.pageId);
            if (pd) { const t = pd.tokens.find((tk) => tk.id === msg.tokenId); if (t) { t.x = msg.x; t.y = msg.y; } }
          }
          break;
        case 'token:created':
          if (msg.pageId === pages.viewingPageId) {
            const exists = tokens.tokens.some(t => t.id === msg.token.id);
            if (!exists) {
              tokens.addTokenDirect({ ...msg.token, imageEl: null });
            }
            // Always ensure token is in dynamic fog ref (creator's own echo may skip addTokenDirect but still needs fog sync)
            if (!dynamicFogTokensRef.current.some(t => t.id === msg.token.id)) {
              dynamicFogTokensRef.current.push(msg.token);
            }
            dynamicFog.invalidateVisibility();
          } else {
            const pd = pages.getPageData(msg.pageId);
            if (pd && !pd.tokens.some(t => t.id === msg.token.id)) {
              pd.tokens.push(msg.token);
            }
          }
          break;
        case 'token:updated':
          if (msg.pageId === pages.viewingPageId) {
            tokens.updateToken(msg.tokenId, msg.changes);
            // Sync dynamic fog tokens ref (vision, conditions, etc.)
            const updatedFogToken = dynamicFogTokensRef.current.find(t => t.id === msg.tokenId);
            if (updatedFogToken) {
              Object.assign(updatedFogToken, msg.changes);
              if (import.meta.env.DEV && ('conditions' in msg.changes || 'vision' in msg.changes)) {
                console.warn('[fog-sync] token updated:', msg.tokenId.slice(0,8), 'conditions:', updatedFogToken.conditions, 'vision:', updatedFogToken.vision?.enabled);
              }
            }
            dynamicFog.invalidateVisibility();
            // Invalidate lighting if light field changed
            if ('light' in msg.changes) dynamicFog.invalidateLighting();
          } else {
            const pd = pages.getPageData(msg.pageId);
            if (pd) { const t = pd.tokens.find((tk) => tk.id === msg.tokenId); if (t) Object.assign(t, msg.changes); }
          }
          break;
        case 'token:deleted':
          if (msg.pageId === pages.viewingPageId) {
            tokens.removeToken(msg.tokenId);
            dynamicFogTokensRef.current = dynamicFogTokensRef.current.filter(t => t.id !== msg.tokenId);
            dynamicFog.invalidateVisibility();
          } else {
            const pd = pages.getPageData(msg.pageId);
            if (pd) pd.tokens = pd.tokens.filter((tk) => tk.id !== msg.tokenId);
          }
          break;
        case 'token:aura-added': {
          if (msg.pageId === pages.viewingPageId) {
            const tkn = tokens.tokens.find((tk) => tk.id === msg.tokenId);
            if (tkn) tokens.updateToken(msg.tokenId, { auras: [...tkn.auras, msg.aura] });
          } else {
            const pd = pages.getPageData(msg.pageId);
            if (pd) { const tk = pd.tokens.find((t) => t.id === msg.tokenId); if (tk) tk.auras.push(msg.aura); }
          }
          break;
        }
        case 'token:aura-updated': {
          if (msg.pageId === pages.viewingPageId) {
            const tkn = tokens.tokens.find((tk) => tk.id === msg.tokenId);
            if (tkn) tokens.updateToken(msg.tokenId, { auras: tkn.auras.map((a) => a.id === msg.aura.id ? msg.aura : a) });
          } else {
            const pd = pages.getPageData(msg.pageId);
            if (pd) { const tk = pd.tokens.find((t) => t.id === msg.tokenId); if (tk) { const idx = tk.auras.findIndex((a) => a.id === msg.aura.id); if (idx >= 0) tk.auras[idx] = msg.aura; } }
          }
          break;
        }
        case 'token:aura-removed': {
          if (msg.pageId === pages.viewingPageId) {
            const tkn = tokens.tokens.find((tk) => tk.id === msg.tokenId);
            if (tkn) tokens.updateToken(msg.tokenId, { auras: tkn.auras.filter((a) => a.id !== msg.auraId) });
          } else {
            const pd = pages.getPageData(msg.pageId);
            if (pd) { const tk = pd.tokens.find((t) => t.id === msg.tokenId); if (tk) tk.auras = tk.auras.filter((a) => a.id !== msg.auraId); }
          }
          break;
        }
        case 'chat:received':
          chat.addMessage(msg.message);
          if (msg.message.type === 'roll') {
            sounds.playDiceSound();
          } else if (msg.message.type !== 'system') {
            sounds.playMessageSound();
          }
          break;
        case 'chat:image-update':
          chat.updateMessage(msg.messageId, {
            imageStatus: msg.imageStatus,
            ...(msg.imageUrl ? { imageUrl: msg.imageUrl } : {}),
          });
          break;
        case 'chat:ai-typing': {
          const phase = msg.typing
            ? (msg.phase as 'connecting' | 'thinking' | 'generating') || 'connecting'
            : false;
          chat.setAiTyping(phase);
          // Clear any existing timeout
          if (aiTypingTimeoutRef.current) {
            clearTimeout(aiTypingTimeoutRef.current);
            aiTypingTimeoutRef.current = null;
          }
          // Auto-hide after 30s fallback if typing
          if (phase) {
            aiTypingTimeoutRef.current = setTimeout(() => {
              chat.setAiTyping(false);
              aiTypingTimeoutRef.current = null;
            }, 30_000);
          }
          break;
        }
        case 'drawing:added':
          if (msg.pageId === pages.viewingPageId) { drawing.strokes.current.push(msg.stroke); drawing.invalidateCache(); engine.markDirty('drawing'); }
          else { const pd = pages.getPageData(msg.pageId); if (pd) pd.drawings.push(msg.stroke); }
          break;
        case 'drawing:erased':
          if (msg.pageId === pages.viewingPageId) {
            drawing.strokes.current = msg.strokeId
              ? drawing.strokes.current.filter((stroke) => stroke.id !== msg.strokeId)
              : drawing.strokes.current.filter((_, index) => index !== msg.strokeIndex);
            drawing.invalidateCache();
            engine.markDirty('drawing');
          }
          else {
            const pd = pages.getPageData(msg.pageId);
            if (pd) {
              pd.drawings = msg.strokeId
                ? pd.drawings.filter((stroke) => stroke.id !== msg.strokeId)
                : pd.drawings.filter((_, index) => index !== msg.strokeIndex);
            }
          }
          break;
        case 'drawing:moved':
          if (msg.pageId === pages.viewingPageId) {
            const moveIdSet = new Set(msg.strokeIds as string[]);
            for (const stroke of drawing.strokes.current) {
              if (!moveIdSet.has(stroke.id)) continue;
              if (stroke.type === 'path' && stroke.points) {
                for (const p of stroke.points) { p.x += msg.dx; p.y += msg.dy; }
              }
              if (stroke.x1 !== undefined) stroke.x1 += msg.dx;
              if (stroke.y1 !== undefined) stroke.y1 += msg.dy;
              if (stroke.x2 !== undefined) stroke.x2 += msg.dx;
              if (stroke.y2 !== undefined) stroke.y2 += msg.dy;
            }
            drawing.invalidateCache();
            engine.markDirty('drawing');
          } else {
            const pd = pages.getPageData(msg.pageId);
            if (pd) {
              const moveIdSet = new Set(msg.strokeIds as string[]);
              for (const d of pd.drawings) {
                if (!moveIdSet.has(d.id)) continue;
                if (d.type === 'path' && d.points) {
                  for (const p of d.points) { p.x += msg.dx; p.y += msg.dy; }
                }
                if (d.x1 !== undefined) d.x1 += msg.dx;
                if (d.y1 !== undefined) d.y1 += msg.dy;
                if (d.x2 !== undefined) d.x2 += msg.dx;
                if (d.y2 !== undefined) d.y2 += msg.dy;
              }
            }
          }
          break;
        case 'drawing:deleted':
          if (msg.pageId === pages.viewingPageId) {
            const deleteIdSet = new Set(msg.strokeIds as string[]);
            drawing.strokes.current = drawing.strokes.current.filter(s => !deleteIdSet.has(s.id));
            drawing.invalidateCache();
            engine.markDirty('drawing');
          } else {
            const pd = pages.getPageData(msg.pageId);
            if (pd) {
              const deleteIdSet = new Set(msg.strokeIds as string[]);
              pd.drawings = pd.drawings.filter(d => !deleteIdSet.has(d.id));
            }
          }
          break;
        case 'drawing:cleared':
          if (msg.pageId === pages.viewingPageId) { drawing.strokes.current = []; drawing.invalidateCache(); engine.markDirty('drawing'); }
          else { const pd = pages.getPageData(msg.pageId); if (pd) pd.drawings = []; }
          break;
        case 'fog:revealed':
          if (msg.pageId === pages.viewingPageId) { fog.revealed.current.push(msg.reveal); fog.invalidateCache(); engine.markDirty('fog'); }
          else { const pd = pages.getPageData(msg.pageId); if (pd) pd.fog.revealed.push(msg.reveal); }
          break;
        case 'fog:revealed-batch':
          if (msg.pageId === pages.viewingPageId) { fog.revealed.current.push(...msg.reveals); fog.invalidateCache(); engine.markDirty('fog'); }
          else { const pd = pages.getPageData(msg.pageId); if (pd) pd.fog.revealed.push(...msg.reveals); }
          break;
        case 'fog:reset':
          if (msg.pageId === pages.viewingPageId) { fog.revealed.current = []; fog.closings.current = []; fog.invalidateCache(); dynamicFog.setExplored([]); engine.markDirty('fog'); }
          else { const pd = pages.getPageData(msg.pageId); if (pd) { pd.fog.revealed = []; pd.fog.closings = []; pd.fog.explored = []; } }
          break;
        case 'fog:toggled':
          if (msg.pageId === pages.viewingPageId) { fog.enabled.current = msg.enabled; fog.invalidateCache(); engine.markDirty('fog'); }
          else { const pd = pages.getPageData(msg.pageId); if (pd) pd.fog.enabled = msg.enabled; }
          break;
        case 'fog:closed':
          if (msg.pageId === pages.viewingPageId) { fog.closings.current.push(msg.closing); fog.invalidateCache(); engine.markDirty('fog'); }
          else { const pd = pages.getPageData(msg.pageId); if (pd) pd.fog.closings.push(msg.closing); }
          break;
        case 'fog:closed-batch':
          if (msg.pageId === pages.viewingPageId) { fog.closings.current.push(...msg.closings); fog.invalidateCache(); engine.markDirty('fog'); }
          else { const pd = pages.getPageData(msg.pageId); if (pd) pd.fog.closings.push(...msg.closings); }
          break;
        case 'fog:covered-all':
          if (msg.pageId === pages.viewingPageId) { fog.revealed.current = []; fog.closings.current = []; fog.enabled.current = true; fog.invalidateCache(); engine.markDirty('fog'); }
          else { const pd = pages.getPageData(msg.pageId); if (pd) { pd.fog.revealed = []; pd.fog.closings = []; pd.fog.enabled = true; } }
          break;
        case 'fog:cleared-all':
          // «Убрать всё» стирает и explored (как fog:reset) — иначе у не-инициаторов
          // авто-разведанный dynamic-туман живёт до перезагрузки.
          if (msg.pageId === pages.viewingPageId) { fog.revealed.current = []; fog.closings.current = []; fog.enabled.current = false; fog.invalidateCache(); dynamicFog.setExplored([]); engine.markDirty('fog'); }
          else { const pd = pages.getPageData(msg.pageId); if (pd) { pd.fog.revealed = []; pd.fog.closings = []; pd.fog.explored = []; pd.fog.enabled = false; } }
          break;
        case 'initiative:updated':
          initiative.setEntries(msg.entries);
          initiative.setCurrentIndex(msg.currentIndex);
          initiative.setRound(msg.round);
          break;
        case 'initiative:panel-updated':
          onInitiativePanelToggle?.(msg.visible);
          break;
        case 'note:created':
          if (msg.note.pageId === pages.viewingPageId) { notesHook.setNotes((prev) => [...prev, msg.note]); engine.markDirty('token'); }
          else { const pd = pages.getPageData(msg.note.pageId); if (pd) pd.notes = [...(pd.notes ?? []), msg.note]; }
          break;
        case 'note:updated':
          if (msg.pageId === pages.viewingPageId) { notesHook.setNotes((prev) => prev.map((n) => n.id === msg.noteId ? { ...n, ...msg.changes } : n)); engine.markDirty('token'); }
          else { const pd = pages.getPageData(msg.pageId); if (pd) pd.notes = (pd.notes ?? []).map((n) => n.id === msg.noteId ? { ...n, ...msg.changes } : n); }
          break;
        case 'note:deleted':
          if (msg.pageId === pages.viewingPageId) { notesHook.setNotes((prev) => prev.filter((n) => n.id !== msg.noteId)); engine.markDirty('token'); }
          else { const pd = pages.getPageData(msg.pageId); if (pd) pd.notes = (pd.notes ?? []).filter((n) => n.id !== msg.noteId); }
          break;
        case 'note:moved':
          if (msg.pageId === pages.viewingPageId) { notesHook.setNotes((prev) => prev.map((n) => n.id === msg.noteId ? { ...n, x: msg.x, y: msg.y } : n)); engine.markDirty('token'); }
          else { const pd = pages.getPageData(msg.pageId); if (pd) { const n = (pd.notes ?? []).find((note) => note.id === msg.noteId); if (n) { n.x = msg.x; n.y = msg.y; } } }
          break;
        case 'pin:created':
          if (msg.pin.pageId === pages.viewingPageId) { pinsHook.setPins((prev) => [...prev, msg.pin]); engine.markDirty('token'); }
          else { const pd = pages.getPageData(msg.pin.pageId); if (pd) pd.pins = [...(pd.pins ?? []), msg.pin]; }
          break;
        case 'pin:updated':
          if (msg.pageId === pages.viewingPageId) pinsHook.setPins((prev) => prev.map((p) => p.id === msg.pinId ? { ...p, ...msg.changes } : p));
          else { const pd = pages.getPageData(msg.pageId); if (pd) pd.pins = (pd.pins ?? []).map((p) => p.id === msg.pinId ? { ...p, ...msg.changes } : p); }
          break;
        case 'pin:deleted':
          if (msg.pageId === pages.viewingPageId) pinsHook.setPins((prev) => prev.filter((p) => p.id !== msg.pinId));
          else { const pd = pages.getPageData(msg.pageId); if (pd) pd.pins = (pd.pins ?? []).filter((p) => p.id !== msg.pinId); }
          break;
        case 'pin:moved':
          if (msg.pageId === pages.viewingPageId) pinsHook.setPins((prev) => prev.map((p) => p.id === msg.pinId ? { ...p, x: msg.x, y: msg.y } : p));
          else { const pd = pages.getPageData(msg.pageId); if (pd) { const pin = (pd.pins ?? []).find((p) => p.id === msg.pinId); if (pin) { pin.x = msg.x; pin.y = msg.y; } } }
          break;
        case 'map:changed':
          if (msg.pageId === pages.viewingPageId) engine.loadMap(msg.url);
          pages.handleMapChanged(msg.pageId, msg.url);
          break;
        case 'page:list': pages.handlePageList(msg.pages, msg.activePageId); break;
        case 'page:created': pages.handlePageCreated(msg.page); toast.success(`${msg.page.name} ${t('session.pageCreated')}`); break;
        case 'page:renamed': pages.handlePageRenamed(msg.pageId, msg.name); break;
        case 'page:settings-updated': {
          const pd = pages.getPageData(msg.pageId);
          if (pd) {
            if (msg.changes.gridSize !== undefined) pd.gridSize = msg.changes.gridSize;
            if (msg.changes.mapScale !== undefined) (pd as unknown as Record<string, unknown>).mapScale = msg.changes.mapScale;
            if (msg.changes.bgColor !== undefined) (pd as unknown as Record<string, unknown>).bgColor = msg.changes.bgColor;
            if (msg.changes.globalIllumination !== undefined) {
              pd.globalIllumination = { ...(pd.globalIllumination ?? { level: 'bright' as const }), ...msg.changes.globalIllumination } as import('@roll1/shared').GlobalIllumination;
            }
          }
          if (msg.pageId === pages.viewingPageId) {
            if (msg.changes.gridSize !== undefined) engine.gridSizeRef.current = msg.changes.gridSize;
            if (msg.changes.mapScale !== undefined) engine.mapScaleRef.current = msg.changes.mapScale;
            if (msg.changes.bgColor !== undefined) engine.bgColorRef.current = msg.changes.bgColor || null;
            if (msg.changes.globalIllumination !== undefined) {
              globalIlluminationRef.current = pd!.globalIllumination!;
              dynamicFog.invalidateLighting();
            }
            engine.markAllDirty();
          }
          break;
        }
        case 'page:deleted': pages.handlePageDeleted(msg.pageId); break;
        case 'page:activated':
          saveCurrentPageToStore();
          pages.handlePageActivated(msg.pageId, msg.pageState);
          break;
        case 'page:reordered': pages.handlePageReordered(msg.pageIds); break;
        case 'page:tokens-transferred':
          pages.handleTokensTransferred(msg.sourcePageId, msg.targetPageId, msg.tokenIds, msg.tokens);
          if (msg.sourcePageId === pages.viewingPageId || msg.targetPageId === pages.viewingPageId) {
            const page = pages.getPageData(pages.viewingPageId);
            if (page) { tokens.setTokens(page.tokens.map((t) => ({ ...t, imageEl: null }))); engine.markAllDirty(); }
          }
          break;
        case 'sheet:created': charSheets.handleCreated(msg.sheet); toast.success(`${msg.sheet.name} ${t('session.sheetCreated')}`); break;
        case 'sheet:updated': charSheets.handleUpdated(msg.sheetId, msg.changes, msg.abilities); break;
        case 'sheet:deleted': charSheets.handleDeleted(msg.sheetId); break;
        case 'sheet:group-updated': charSheets.setSheets(prev => prev.map(sh => sh.id === msg.sheetId ? { ...sh, group: msg.group || undefined } : sh)); break;
        case 'sheet:visibility-changed': charSheets.setSheets(prev => prev.map(sh => sh.id === msg.sheetId ? { ...sh, visible: msg.visible } : sh)); break;
        case 'sheet:owner-changed': charSheets.handleOwnerChanged(msg.sheetId, msg.ownerName); break;
        case 'sheet:public-fields-updated': charSheets.handlePublicFieldsUpdated(msg.sheetId, msg.publicFields, msg.visibleValues); break;
        case 'sheet:token-bound': charSheets.handleTokenBound(msg.sheetId, msg.tokenId); break;
        case 'sheet:token-unbound': charSheets.handleTokenUnbound(msg.sheetId); break;
        case 'sheet:repeating-row-added': charSheets.handleRepeatingRowAdded(msg.sheetId, msg.sectionKey, msg.row); break;
        case 'sheet:repeating-row-removed': charSheets.handleRepeatingRowRemoved(msg.sheetId, msg.sectionKey, msg.rowId); break;
        case 'sheet:repeating-row-updated': charSheets.handleRepeatingRowUpdated(msg.sheetId, msg.sectionKey, msg.rowId, msg.changes); break;
        case 'sheet:spell-slots-updated': if (gameSystem === 'dnd5e') charSheets.handleSpellSlotsUpdated(msg.sheetId, msg.spellSlots); break;
        case 'sheet:rest-completed': charSheets.handleRestCompleted(msg.sheetId, msg.restType, msg.spellSlots, msg.hp, msg.hitDice); break;
        case 'sheet:hit-dice-result': if (gameSystem === 'dnd5e') charSheets.handleHitDiceResult(msg.sheetId, msg.newHp, msg.hitDiceUsed); break;
        case 'sheet:token-actions-updated': charSheets.handleTokenActionsUpdated(msg.sheetId, msg.tokenActions); break;
        case 'sheet:portrait-updated': charSheets.handlePortraitUpdated(msg.sheetId, msg.portrait); break;
        case 'sheet:token-description-updated': charSheets.handleTokenDescriptionUpdated(msg.sheetId, msg.tokenDescription); break;
        case 'sheet:effects-updated': charSheets.handleEffectsUpdated(msg.sheetId, msg.activeEffects, msg.values); break;
        case 'sheet:spellbook-updated': charSheets.handleSpellbookUpdated(msg.sheetId, msg.spellbook); break;
        case 'sheet:concentration-updated': if (gameSystem === 'dnd5e') charSheets.handleConcentrationUpdated(msg.sheetId, msg.concentrationSpellId); break;
        case 'sheet:level-up-complete': if (gameSystem === 'dnd5e') charSheets.handleLevelUpComplete(msg.sheetId, msg.values, msg.abilities, msg.hitDice, msg.spellSlots, msg.spellbook, msg.classData); break;
        case 'sheet:list': charSheets.setSheets(msg.sheets); break;
        case 'template:created': tmpl.handleCreated(msg.template); break;
        case 'template:updated': tmpl.handleUpdated(msg.templateId, msg.changes); break;
        case 'template:deleted': tmpl.handleDeleted(msg.templateId); break;
        case 'template:list': tmpl.setTemplates(msg.templates); break;
        case 'roll:result': rolls.handleRollResult(msg.rollResult); chat.addSheetRollMessage(msg.rollResult); sounds.playDiceSound(); break;
        case 'ruler:shown': drawing.showPublicRuler({ by: msg.by, points: msg.points, totalDistance: msg.totalDistance, hideAt: msg.persistent ? 0 : Date.now() + 5000 }); break;
        case 'ruler:hidden': drawing.hidePublicRuler(msg.by); break;
        case 'ping:showed': onPing?.(msg.x, msg.y, msg.by, msg.color); break;
        case 'settings:updated': setSessionSettings(msg.settings); break;
        case 'location:link-created':
          pages.handleLocationLinkCreated(msg.pageId, msg.link);
          if (msg.pageId === pages.viewingPageId) { const pd = pages.getPageData(msg.pageId); if (pd) locationLinks.setLinks(pd.locationLinks); }
          break;
        case 'location:link-updated':
          pages.handleLocationLinkUpdated(msg.pageId, msg.link);
          if (msg.pageId === pages.viewingPageId) { const pd = pages.getPageData(msg.pageId); if (pd) locationLinks.setLinks(pd.locationLinks); }
          break;
        case 'location:link-deleted':
          pages.handleLocationLinkDeleted(msg.pageId, msg.linkId);
          if (msg.pageId === pages.viewingPageId) { const pd = pages.getPageData(msg.pageId); if (pd) locationLinks.setLinks(pd.locationLinks); }
          break;
        case 'map-object:created':
          if (msg.pageId === pages.viewingPageId) {
            const objs = mapObjects.objects.current;
            if (!objs.some(o => o.id === msg.mapObject.id)) {
              mapObjects.setObjects([...objs, msg.mapObject]);
            }
            // Invalidate lighting if the new object is a light source
            if (msg.mapObject.category === 'light') dynamicFog.invalidateLighting();
          }
          break;
        case 'map-object:updated':
          if (msg.pageId === pages.viewingPageId) {
            const oldObj = mapObjects.objects.current.find(o => o.id === msg.objectId);
            mapObjects.setObjects(
              mapObjects.objects.current.map(o =>
                o.id === msg.objectId ? { ...o, ...msg.changes } : o,
              ),
            );
            // Invalidate lighting if a light-category object was changed (state, light props, category)
            if (oldObj?.category === 'light' || msg.changes.category === 'light') dynamicFog.invalidateLighting();
          }
          break;
        case 'map-object:deleted':
          if (msg.pageId === pages.viewingPageId) {
            const deletedObj = mapObjects.objects.current.find(o => o.id === msg.objectId);
            mapObjects.setObjects(
              mapObjects.objects.current.filter(o => o.id !== msg.objectId),
            );
            // Invalidate lighting if deleted object was a light source
            if (deletedObj?.category === 'light') dynamicFog.invalidateLighting();
          }
          break;
        case 'map-object:reordered':
          if (msg.pageId === pages.viewingPageId) {
            const ids: string[] = msg.objectIds;
            const current = mapObjects.objects.current;
            const byId = new Map(current.map(o => [o.id, o]));
            const reordered = ids.map((id, i) => {
              const obj = byId.get(id);
              return obj ? { ...obj, sortOrder: i } : null;
            }).filter((o): o is NonNullable<typeof o> => o !== null);
            mapObjects.setObjects(reordered);
          }
          break;
        case 'location:navigation-mode-changed': pages.handleNavigationModeChanged(msg.mode); break;
        case 'location:navigated': break;
        case 'location:navigate-request': break;
        case 'alert:created':
          alertCtx.setAlerts(prev => [...prev, msg.alert]);
          break;
        case 'alert:updated':
          alertCtx.setAlerts(prev => prev.map(a => a.id === msg.alertId ? { ...a, ...msg.changes } : a));
          break;
        case 'alert:deleted':
          alertCtx.setAlerts(prev => prev.filter(a => a.id !== msg.alertId));
          break;
        case 'alert:fired':
          alertCtx.queue.enqueue(msg.alert);
          break;
        case 'alert:reordered':
          alertCtx.setAlerts(prev => {
            const newAlerts = [...prev];
            for (let i = 0; i < msg.alertIds.length; i++) {
              const a = newAlerts.find(x => x.id === msg.alertIds[i]);
              if (a) a.sortOrder = i;
            }
            return newAlerts;
          });
          break;
        // Wall messages (Dynamic Lighting)
        case 'wall:created':
        case 'wall:updated':
        case 'wall:deleted':
        case 'walls:created':
        case 'wall:auto-synced':
          wallsHook.handleMessage(msg);
          dynamicFog.invalidateVisibility();
          dynamicFog.invalidateLighting();
          break;
        case 'door:toggled':
          wallsHook.handleMessage(msg);
          dynamicFog.invalidateVisibility();
          dynamicFog.invalidateLighting();
          break;
        case 'session:error': chat.addSystemMessage(msg.message); toast.error(msg.message); break;
        case 'command:error': {
          // Roll back an optimistic ghost the server rejected. token:create adds
          // the token locally before the server acks; on rejection (permission
          // denied, invalid page) the ghost otherwise lingered on the actor's
          // canvas forever. entityId is the exact token to drop.
          if (msg.command === 'token:create' && msg.entityId) {
            const ghostId = msg.entityId;
            tokens.removeToken(ghostId);
            // Also drop the matching undo command — otherwise Ctrl+Z would
            // "undo" the already-removed ghost (wasting a slot) and Redo would
            // re-summon it into another rejection.
            undoStack.dropByEntityId(ghostId);
            // Sweep page stores: if the player switched pages during the
            // create→reject round-trip, the ghost was snapshotted into the
            // origin page's store, which removeToken (live page only) misses —
            // it would reload the moment the player returns to that page.
            for (const p of pages.pages) {
              const pd = pages.getPageData(p.id);
              if (pd && pd.tokens.some((tk) => tk.id === ghostId)) {
                pages.setPageData(p.id, { ...pd, tokens: pd.tokens.filter((tk) => tk.id !== ghostId) });
              }
            }
          }
          const key = `${msg.command}:${msg.code ?? ''}:${msg.error}`;
          const now = Date.now();
          const last = lastCommandErrorRef.current;
          if (!last || last.key !== key || now - last.ts > 1500) {
            const errorKey = msg.code ? `error.${msg.code}` : '';
            const translated = errorKey ? t(errorKey as keyof TranslationKeys) : msg.error;
            const errorText = (translated && translated !== errorKey) ? translated : msg.error;
            toast.error(errorText);
            lastCommandErrorRef.current = { key, ts: now };
          }
          break;
        }
        case 'session:created':
          toast.success(t('session.sessionCreated'));
          if (msg.journalEntries) journal.setEntries(msg.journalEntries);
          break;
        case 'session:joined':
          toast.success(t('session.sessionJoined'));
          if (msg.journalEntries) journal.setEntries(msg.journalEntries);
          break;
        // Journal messages
        case 'journal:created': journal.handleCreated(msg.entry); break;
        case 'journal:updated': journal.handleUpdated(msg.entryId, msg.changes); break;
        case 'journal:deleted': journal.handleDeleted(msg.entryId); break;
        case 'journal:revealed': journal.handleRevealed(msg.entry); onJournalRevealed?.(); break;
        case 'journal:pinned': journal.handlePinned(msg.entryId, msg.pinned); break;
        case 'journal:personal-created': journal.handlePersonalCreated(msg.entry); break;
        case 'journal:linked': journal.handleLinked(msg.link); break;
        case 'journal:unlinked': journal.handleUnlinked(msg.sourceId, msg.targetId, msg.relation); break;
        case 'state:batch-update': {
          // ADR-008: Reconcile handler — must cover all domains used in batch scenarios.
          const bu = msg as import('@roll1/shared').BatchUpdateEvent;
          // Update page store for affected pages
          for (const pid of bu.affected.pages) {
            const freshPage = bu.state.pages.find(p => p.id === pid);
            if (freshPage) pages.setPageData(pid, freshPage);
          }
          // Reload current page hooks if it was affected
          if (bu.affected.pages.includes(pages.viewingPageId)) {
            const currentPage = bu.state.pages.find(p => p.id === pages.viewingPageId);
            if (currentPage) loadPageToHooks(currentPage);
          }
          // Update character sheets
          if (bu.affected.sheets.length > 0) {
            charSheets.setSheets(bu.state.characterSheets);
          }
          // Journal full sync (undo): replace all entries with the restored set
          if ((bu as any).journalFullSync && bu.journalEntries) {
            journal.setEntries(bu.journalEntries as import('@roll1/shared').JournalEntry[]);
          } else if (bu.journalEntries?.length) {
            // Incremental: add new entries
            for (const entry of bu.journalEntries) {
              journal.handleCreated(entry);
            }
          }
          break;
        }
        case 'participant:joined':
          if (msg.participant.id !== mp.participant?.id) toast.info(`${msg.participant.name} ${t('session.participantJoined')}`);
          break;
        case 'participant:left': {
          const lp = mp.participants.find((p) => p.id === msg.participantId);
          if (lp && lp.id !== mp.participant?.id) toast.info(`${lp.name} ${t('session.participantLeft')}`);
          break;
        }
      }
    });
  }, [mp, tokens, chat, drawing, fog, initiative, engine, pages, charSheets, tmpl, rolls, sounds, saveCurrentPageToStore, locationLinks, mapObjects, notesHook, pinsHook, setSessionSettings, alertCtx, journal, onInitiativePanelToggle, onPing, wallsHook]); // eslint-disable-line

  return {
    saveCurrentPageToStore,
    loadPageToHooks,
    serializeToken,
    fogSentEnabled,
  };
}
